/**
 * Diner-facing copy for payment and checkout failures — TEAM-13884, TEAM-12668.
 *
 * This file holds the **values**. It is split into two groups because they reach the diner by
 * different routes and therefore carry slightly different contracts:
 *
 * - `PAYMENT_ERROR_STATICS` — **static messages** in the render rule (`resolveDinerMessage`,
 *   `@/utils/fpoErrors`): the call site's own message, used when a failure carries nothing more
 *   specific to resolve from — no coo-api error code, no catalogued slug, no `user_message`.
 *   Raised at a component or hook. (Written as "tier-3" under the five-tier rule; TEAM-15059
 *   inserted a slug tier at position 2, making it tier 4 of six. Cite
 *   docs/DINER_ERROR_MESSAGES.md#the-render-rule rather than an index — this one has moved once
 *   already.)
 * - `DECLINE_ERROR_COPY` — the copy `resolveDeclineCopy` (`@/utils/payment`) returns when a
 *   provider's raw `detail` names a card decline. Raised only in `redux/orderStatus/saga.ts`.
 *
 * Both are exported as one `PAYMENT_ERROR_COPY` so call sites and readers have a single catalog of
 * diner payment copy, which is what docs/PAYMENT_PROVIDERS.md promises. The groups are exported
 * separately as well, because `utils/__tests__/paymentErrorCopy.ts` has one constraint that applies
 * to the statics only — see `DECLINE_ERROR_COPY` below.
 *
 * They are deliberately not in `FPO_ERROR_COPY` — that catalog is keyed by numeric FPO code and its
 * test asserts exactly 37 contiguous keys, so a non-code-keyed entry breaks it. This follows the
 * `INITIALIZATION_ERROR_EXPLANATIONS` precedent instead: copy stays out of the catalog, adopts its
 * constraints, and is pinned by its own test.
 *
 * One map rather than a literal per call site so that AC #4 — "the same message at all the
 * payment-form sites, so copy does not vary by provider" — holds by construction. There were four
 * when TEAM-13884 wrote that; TEAM-15073 added Toast as the fifth, which is the point of sharing a
 * constant rather than counting literals.
 *
 * **Never make a charge-state claim here.** A provider can place an authorization before a submit
 * fails, so "your card hasn't been charged" is false whenever a pending hold lands on the diner's
 * statement. The per-provider authorization/capture audit that would let us say otherwise is
 * TEAM-15074, and it is not done. See `types/orderStatus.ts` (`ORDER_SUBMISSION_ERROR`).
 *
 * Every other constraint — no markup, length, terminal punctuation, and the two substring tables
 * these sentences must not collide with — is stated and enforced in
 * `utils/__tests__/paymentErrorCopy.ts`. Read that file before adding a key.
 *
 * @see docs/DINER_ERROR_COPY.md
 */
export const PAYMENT_ERROR_STATICS = {
  /**
   * A card payment was attempted and did not complete, and retrying the same card cannot help —
   * so it points the diner at their card details. The default for a card failure: TEAM-14895's
   * classifier returns it for a terminal Toast decline AND for one it cannot classify, because a
   * diner wrongly sent back to card entry loses one step while a diner wrongly told to retry burns
   * attempts on a card that will never authorize.
   *
   * Makes no charge-state claim, by design.
   */
  cardPaymentFailed:
    "Your payment didn't go through. Check your card details or try a different card.",
  /**
   * The same failure when the card was never actually judged — a transport failure, or a provider
   * that explicitly said to retry — so the same card is untested and pressing again can work.
   * TEAM-14895 AC3/AC4: the retryable half of the pair `getToastDeclineCopy` chooses between.
   *
   * Written in `cardPaymentFailed`'s voice on purpose: the two differ only in the next step they
   * name, which is the whole distinction the diner is meant to read. Makes no charge-state claim
   * either — a failure after authorization but before capture can still leave a hold.
   */
  cardPaymentFailedRetryable: "Your payment didn't go through. Please try again.",
  /**
   * The diner's bank asked them to verify the card (3D Secure / SCA) and the challenge did not
   * complete — they closed it, failed it, or Stripe.js could not run it. TEAM-14331 AC3.
   *
   * Deliberately does not say the card was declined or push the diner off it: the card is good, and
   * some issuers challenge every card they issue, so "use a different card" is not always an option.
   * Re-entering the same card starts a fresh challenge. Its one call site
   * (`redux/orderStatus/paymentVerificationSaga.ts`) dispatches `paymentTypeReset()` alongside it,
   * so the card form this sentence points at is the one on screen.
   */
  cardVerificationFailed:
    "Your bank couldn't verify this card. Please re-enter your card to try again, or use a different card.",
  /** The wallet exists but is not usable yet — a reload can fix it. */
  walletNotReady:
    "Apple Pay / Google Pay isn't ready yet. Refresh the page or pay with a card instead.",
  /**
   * The wallet was authorized but the payment could not be taken. Points the diner at card entry
   * rather than back at the wallet: its one call site (`stripe/digital-wallet/hooks.ts`) settles
   * the sheet as `'fail'` through `completeWalletEvent` and dispatches `paymentTypeReset()` —
   * deliberately, so the diner is not blocked, matching the `paymentTypeReset` a few lines below it
   * in that file — which unmounts the wallet button in `components/checkout/submitOrder`.
   * "Try again" promised a control that is no longer on screen. Pinned by
   * `stripe/digital-wallet/hooks.test.ts`.
   */
  walletPaymentFailed:
    "We couldn't process your digital wallet payment. Please pay with a card instead.",
  /**
   * A Square digital-wallet attempt failed mid-flow before any token existed — `tokenize()` either
   * rejected (Square's `PaymentMethodAlreadyDestroyedError`, "This instance of Apple Pay/Google Pay
   * has already been destroyed", is the confirmed trigger) or resolved with a status that was
   * neither 'OK' nor the diner's own 'Cancel'. Distinct from `walletPaymentFailed`: that one settles
   * an *authorized* wallet as failed and unmounts the button, so it can only point at card entry.
   * Here `square/digitalWallets/hooks.ts` rebuilds the wallet — the same `paymentTypeReset` + 750ms
   * `digitalWalletToggle` the SDK's own 'Cancel' path runs — so the button returns and the single
   * "try again" step is real once the message lands at the end of that window. Names the failed
   * action then one next step, per docs/DINER_ERROR_COPY.md. Pinned by
   * `square/digitalWallets/hooks.test.ts`. TEAM-15053.
   */
  walletPaymentInterrupted:
    "Your Apple Pay / Google Pay payment didn't go through. Please try again.",
  /** Wallets cannot be offered here at all — a reload will not help, so it does not suggest one. */
  walletUnavailable:
    "Apple Pay and Google Pay aren't available here right now. Please pay with a card instead.",
  /**
   * The provider cannot initialise, usually because the location's payment configuration is
   * incomplete. Says nothing about store configuration or missing information: that is a Chowly/
   * restaurant problem and naming it blames the restaurant in front of its own customer (AC #3).
   */
  providerUnavailable:
    "This restaurant can't take online payments right now. Please try again later or contact them directly.",
  /**
   * The payment form failed to load. One string for all five providers — AC #4.
   *
   * Names the cause only, because every site that uses it has a control on screen that owns the
   * next step: all five render `components/checkout/payment/payment-form-load-error.tsx`, which
   * pairs this title with a `description` and a Refresh or Try again button, and the
   * `square/hooks.ts` / `stripe/hooks.ts` toasts fire on the *same* query failure that puts that
   * panel on the page. Carrying "please refresh" here printed the instruction two or three times
   * in one view, and contradicted Square's button — which calls `refetch()`, not a page reload.
   *
   * Toast is the fifth, added by TEAM-15073 (`components/checkout/payment/index.tsx`), and it is
   * the one site where the panel was missing entirely rather than merely untested: the Toast
   * branch had no failed state, so a blocked SDK left a dimmed spinner and no text at all. Its
   * Refresh control calls `router.reload()`, following `components/checkout/submitOrder`.
   */
  formLoadFailed: "We couldn't load the payment form.",
  /**
   * Same failure, but the one path where nothing else on screen says so: the card element failing
   * to attach in `square/creditCard/form.tsx`. The SDK query succeeded there, so `isError` is false
   * and `PaymentFormLoadError` never renders — this toast is alone and has to carry the next step.
   */
  formLoadFailedStandalone:
    "We couldn't load the payment form. Please refresh the page and try again.",
  /**
   * Names the cause only. Its one render site (`components/checkout/submitOrder`) puts a
   * "Reload Page" button directly beneath it, and a co-rendered control owns the next step — see
   * the copy constraints in docs/DINER_ERROR_COPY.md.
   */
  paymentMethodLoadFailed: "We couldn't load your payment method.",
} as const;

/**
 * TEAM-12668's decline copy: what the diner reads when a provider's raw text names a reason they
 * can act on — a wrong billing ZIP, a mistyped CVV, an expired card.
 *
 * The **match keys** that select these — Square's reason codes, the POS substrings and the two
 * provider regexes — deliberately stay in `@/utils/payment` next to `isPaymentError`, which matches
 * several of the same decline strings in order to route the error and reset the single-use payment
 * token. One list of decline strings, not two that drift apart. Only the values live here, so
 * there is also one governed list of diner payment copy. `resolveDeclineCopy`'s value slot is typed
 * `DeclineCopy`, so a literal cannot be reintroduced at the mapping site.
 *
 * **This group is exempt from the `isPaymentError` constraint that the statics carry, and the
 * exemption is structural rather than a concession.** That constraint exists because
 * `prepareErrorMessage` stringifies a *locally thrown* error into `detail` (`utils/global.ts`), so
 * a static that is thrown rather than dispatched would classify its own failure. These strings are
 * never thrown: `orderSubmissionStartSaga` resolves them from `errorDetail` and `put`s the result
 * as the diner-facing message, and both the routing gate and the token-reset gate read
 * `errorDetail` — never this copy. And were one of them ever to reach `isPaymentError`, matching
 * would be *correct*: it is a decline. Saying so is the whole point of the string.
 *
 * `utils/__tests__/paymentErrorCopy.ts` pins that exemption to exactly the strings
 * `resolveDeclineCopy` can return, in both directions, so a static cannot be filed here to dodge
 * the check. Every other constraint still applies to this group.
 */
export const DECLINE_ERROR_COPY = {
  /** Certainly a decline, but names no reason this maps. Square `GENERIC_DECLINE`; POS catch-all. */
  genericDecline:
    'Your card was declined. Please try a different payment method or contact your bank.',
  /** Square `CVV_FAILURE`; POS `Invalid CVV Code`. */
  cvvMismatch: "The security code on your card didn't match. Please check and try again.",
  /** Square `CARD_EXPIRED`. */
  cardExpired: 'Your card has expired. Please use a different card.',
  /** Square `ADDRESS_VERIFICATION_FAILURE`. */
  billingZipMismatch: "Your card's billing zip didn't match. Please update and try again.",
  /** Square `TRANSACTION_LIMIT`. */
  transactionLimit:
    'Your card has a transaction limit. Please try a smaller amount or a different payment method.',
  /** Square `INSUFFICIENT_FUNDS`. */
  insufficientFunds: 'Your card was declined for insufficient funds. Please try another card.',
  /** Square `CARDHOLDER_INSUFFICIENT_PERMISSIONS`. */
  cardholderRestricted:
    "Your card couldn't be used for this purchase. Please contact your bank or try another card.",
  /** POS `Invalid Billing Address and/or Zip Code`. */
  billingAddressMismatch:
    "Your card's billing address or zip code didn't match. Please check and try again.",
  /** POS `Unsuccessful Payment Transaction`. */
  paymentNotProcessed: "We weren't able to process your payment. Please try again.",
} as const;

/**
 * The value slot of `resolveDeclineCopy`'s mapping tables. Typing the tables with this is what
 * stops a new decline reason being mapped to a literal written at the call site.
 */
export type DeclineCopy = (typeof DECLINE_ERROR_COPY)[keyof typeof DECLINE_ERROR_COPY];

/**
 * Every diner-facing payment string, both groups. Call sites import this; the groups above exist
 * for the one test constraint that distinguishes them.
 */
export const PAYMENT_ERROR_COPY = {
  ...PAYMENT_ERROR_STATICS,
  ...DECLINE_ERROR_COPY,
} as const;
